import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import PreviewFilePDFImage from './PreviewFilePDFImage';
import { useGesture } from '@use-gesture/react';
import PreviewFileNav from './PreviewFileNav';
import classes from './PreviewFile.module.css';
import clsx from 'clsx';

const gapBetweenPages = 24;
const padding = 16;

export default function PreviewFileImage({ url, className, onDownload }) {
    const gestureTargetRef = useRef(null);
    const [pageNum, setPageNum] = useState(1);
    const [zoom, setZoom] = useState(1);
    const [pagesDimensions, setPagesDimensions] = useState({});
    const containerRef = useRef(null);
    const [isScrollEventEnabled, setScrollEventEnabled] = useState(true);

    const handleChangeZoom = (newZoom) => {
        const origin = {
            x: containerRef.current.offsetWidth / 2,
            y: containerRef.current.offsetHeight / 2,
        };

        handleZoom(newZoom, origin);
    };

    const handleZoom = (newZoom, origin) => {
        setScrollEventEnabled(false);

        const scrollLeft = containerRef.current.scrollLeft;
        const scrollTop = containerRef.current.scrollTop;

        setZoom(newZoom);

        const x = (origin.x + scrollLeft) / zoom;
        const y = (origin.y + scrollTop) / zoom;

        const newScrollLeft = x * newZoom - origin.x;
        const newScrollTop = y * newZoom - origin.y;

        containerRef.current.scrollTo({
            top: newScrollTop,
            left: newScrollLeft,
        });

        setTimeout(() => {
            setScrollEventEnabled(true);
        });
    };

    useGesture(
        {
            onPinch: ({ event, offset: [d] }) => {
                event.preventDefault();
                const origin = {
                    x: event.clientX - containerRef.current.offsetLeft,
                    y: event.clientY - containerRef.current.offsetTop,
                };

                const newZoom = Math.min(5, Math.max(0.1, zoom - d / 200));
                handleZoom(newZoom, origin);
            },
            onWheel: ({ event }) => {
                if (event.ctrlKey) {
                    event.preventDefault();
                    const delta = -event.deltaY / 500;
                    const newZoom = Math.max(0.1, zoom + delta);

                    const origin = {
                        x: event.clientX - containerRef.current.offsetLeft,
                        y: event.clientY - containerRef.current.offsetTop,
                    };

                    handleZoom(newZoom, origin);
                }
            },
        },
        { target: gestureTargetRef, wheel: { eventOptions: { passive: false } } },
    );

    useEffect(() => {
        if (!containerRef.current) return;

        const handleScroll = (ev) => {
            if (!isScrollEventEnabled) {
                return;
            }

            const containerScrollTop = ev.target.scrollTop + padding;

            let currentPage = 1;
            let currentOffset = 0;

            for (const page in pagesDimensions) {
                if (currentOffset > containerScrollTop) {
                    break;
                }

                const { height } = pagesDimensions[page];
                const pageHeight = height * zoom;

                currentOffset += pageHeight + gapBetweenPages;

                currentPage = Number(page);
            }

            setPageNum(currentPage);
        };

        const container = containerRef.current;
        container.addEventListener('scroll', handleScroll);

        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [isScrollEventEnabled, pagesDimensions, zoom]);

    const scrollToPage = (pageNumber) => {
        setScrollEventEnabled(false);
        let targetOffset = 0;
        for (let i = 1; i < pageNumber; i++) {
            const pageHeight = pagesDimensions[i].height * zoom;
            targetOffset += pageHeight + gapBetweenPages;
        }

        containerRef.current.scrollTo({ top: targetOffset });

        setTimeout(() => {
            setScrollEventEnabled(true);
        });
        setPageNum(pageNumber);
    };

    return (
        <div className={clsx(classes.layout, className)}>
            <div className={classes.content} ref={containerRef}>
                <div className={classes.pages}>
                    <PreviewFilePDFImage
                        zoom={zoom}
                        url={url}
                        onRender={(dimensions) => {
                            setPagesDimensions({ 1: dimensions });
                        }}
                    />
                    <div className={classes.eventHandler} ref={gestureTargetRef} />
                </div>
            </div>

            <PreviewFileNav
                numPages={1}
                page={pageNum}
                onChangePage={scrollToPage}
                zoom={zoom}
                onChangeZoom={handleChangeZoom}
                onDownload={onDownload}
            />
        </div>
    );
}

PreviewFileImage.propTypes = {
    url: PropTypes.string.isRequired,
    className: PropTypes.string,
    onDownload: PropTypes.func,
};
